import { useTranslation } from 'react-i18next';
import Dialog from './ui/Dialog';
import ActionBar from './ui/ActionBar';
import Button from './ui/Button';
import { LAYERS } from './ui/layers';

/**
 * Reusable "are you sure?" dialog for any destructive delete action across the app.
 * Props:
 *   open        — whether to render
 *   title       — what's being deleted, e.g. "Delete this plate?"
 *   consequences — array of strings describing what will actually happen (cascaded
 *                  deletes, links that will be cleared, blocking conditions, etc.)
 *   deleting    — disables the buttons and shows a "Deleting…" label while in flight
 *   error       — optional error string to show (e.g. after a blocked delete attempt)
 *   onConfirm / onCancel
 *   confirmLabel / confirmingLabel / confirmColor — optional overrides for the confirm
 *                  button's idle/in-flight text and color, for non-delete confirmations
 *                  (e.g. password reset) that reuse this same dialog shape. Default to
 *                  the delete wording/color so every existing call site is unaffected.
 *   children    — optional, shown under the consequences: what the action needs
 *                  from the person, e.g. the reason a DNA identification is withdrawn.
 *   confirmDisabled — optional: the confirm button stays disabled while what
 *                  `children` asks for is missing (DangerZone's `ready`).
 *
 * Built on components/ui/Dialog since 2026-10-18, so Escape cancels, focus
 * goes to the dialog (Cancel, or the first field `children` adds) and comes
 * back when it closes, and Tab stays inside. Escape and a click on the page
 * behind both cancel, except while `deleting`. No x: Cancel is the way out.
 */
export default function ConfirmDeleteModal({
  open, title, consequences, deleting, error, onConfirm, onCancel,
  confirmLabel, confirmingLabel, confirmColor, children, confirmDisabled = false,
}) {
  const { t } = useTranslation('common');
  return (
    <Dialog open={open} title={title} onClose={onCancel} busy={!!deleting} showClose={false}
      titleAs="h3" titleStyle={{ color: 'var(--c-danger-3-fg)' }} layer={LAYERS.confirm} width={440}>
      {consequences && consequences.length > 0 && (
        <ul style={{ fontSize: 13, color: 'var(--c-text-body-fg)', paddingInlineStart: 20, margin: '0 0 16px', lineHeight: 1.5 }}>
          {consequences.map((c, i) => <li key={i} style={{ marginBottom: 4 }}>{c}</li>)}
        </ul>
      )}
      {children}
      {error && (
        <p style={{ fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', borderRadius: 6, padding: '8px 12px', margin: '0 0 16px' }}>
          {error}
        </p>
      )}
      <ActionBar style={{ marginBlockStart: 0 }}>
        <Button onClick={onCancel} disabled={deleting} title={t('tip.cancel')}>
          {t('cancel')}
        </Button>
        <Button variant="danger" onClick={onConfirm} disabled={deleting || confirmDisabled}
          title={confirmLabel ? t('tip.confirmAction') : t('tip.confirmDelete')}
          style={confirmColor ? { background: confirmColor } : undefined}>
          {deleting ? (confirmingLabel || t('deleting')) : (confirmLabel || t('delete'))}
        </Button>
      </ActionBar>
    </Dialog>
  );
}
